import React, { MouseEvent, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import { RightArrowGreenIcon } from "@/components/icons/RightArrowGreenIcon";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useNavigation } from "@/hooks/useNavigation";
import { GA4 } from "@/utils/ga4Utils";
import { cn, isIpad } from "@/utils/general";

import styles from "./NavigationScience.module.scss";

export interface Subitem {
  name?: string;
  url: string;
  image: string;
  alt: string;
  text: string;
  subtext?: string;
}

interface NavigationScienceProps {
  item: { children: Subitem[] };
}

const removeActiveItemClass = (e: MouseEvent, preventClick?: boolean) => {
  const el = e.currentTarget as HTMLElement;
  const mouseLeftItem = hasHover(e);
  if (mouseLeftItem) {
    mouseLeftItem.classList.remove("hover");
  }
  if (preventClick) {
    e.preventDefault();
  }
  handleExpand(el);
};

const handleExpand = (el: HTMLElement) => {
  if (el?.id) {
    document.querySelectorAll("nav.navigation-variant [aria-expanded]").forEach((a) => {
      const isHover = a?.parentElement?.classList.contains("hover");
      (a as HTMLElement).setAttribute("aria-expanded", isHover ? "true" : "false");
    });
  }
};

const hasHover = (e: MouseEvent) => {
  return (e.target as HTMLElement)?.closest(".has-subnav.hover");
};

const buildCls = (subitem: Subitem) => {
  return subitem.subtext ? "has-subtext" : "no-subtext";
};

const trackClicked = (text: string, destination: string) => {
  GA4.onNavigationClicked({
    type: "button",
    text,
    destination,
  });
};

const NavigationScience: React.FC<NavigationScienceProps> = ({ item }) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigation();

  const onTouchStart = (url: string) => {
    if (isIpad()) {
      navigate(url);
    }
  };

  return (
    <div className={cn("dropdown dropdown-science-desktop", styles.dropdownMenu)}>
      <div className={cn("row", styles.dropdownWrapper)}>
        {item.children.map((subitem, idx) => (
          <div
            key={`nav-item-${idx}`}
            className={cn(styles.navItem, subitem.name)}
            onTouchStart={() => onTouchStart(subitem.url)}
          >
            <LinkHandler
              path={subitem.url}
              onClick={() => {
                trackClicked(subitem.name, subitem.url);
              }}
            >
              <div
                className={styles.subItemWrapper}
                onMouseEnter={() => setHoveredItem(subitem.name)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={(e) => removeActiveItemClass(e)}
              >
                <div
                  className={cn(
                    styles.subItem,
                    subitem.name.toLowerCase(),
                    buildCls(subitem),
                    "group",
                    hoveredItem === subitem.name && "hovered"
                  )}
                >
                  <div className="relative w-[200px] h-[200px]">
                    <Picture
                      src={subitem.image}
                      alt={subitem.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className={styles.navItemFooter}>
                    <div className="flex justify-between items-center">
                      <div>
                        <TextElementNova
                          element="h4"
                          text={subitem.name}
                          className="Body-10-Dia-Normal"
                        />
                      </div>
                      <RightArrowGreenIcon />
                    </div>
                    <div className="pt-[4px]">
                      <TextElementNova
                        element="p"
                        text={subitem.text}
                        className="CTA-10-Dia-Normal text-nova-transparency-black-70"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </LinkHandler>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NavigationScience;
